// src/App.js
import { useState, useEffect } from 'react';
import LandingPage from './components/common/LandingPage';
import DevicesMap from './components/common/DevicesMap';
import WeatherAnalyticsDashboard from './components/common/WeatherAnalyticsDashboard';
import WeatherStationDashboard from './components/dashboard/WeatherStationDashboard';
import SuperAdminDashboard from './components/admin/SuperAdminDashboard';
import NearestStationsPanel from './components/common/NearestStationsPanel';
import ProtectedRoute from './components/common/ProtectedRoute';
import SubscriptionPage from './components/common/SubscriptionPage';
import NotificationBell from './components/common/NotificationBell';
import ApiKeysPage from './components/common/ApiKeysPage';
import ProfilePage from './components/profile/ProfilePage';
import MessagingPanel from './components/common/MessagingPanel';
import {
  Shield, MapPin, BarChart3, Activity, Navigation,
  User, LogOut, ChevronLeft, ChevronRight, CreditCard, Key, MessageSquare,
  Map, Thermometer, CheckCircle, Radio, ClipboardList,
} from 'lucide-react';
import { fetchWeatherDevices } from './services/weatherService';
import authService from './services/authService';
import { loadDeviceMeta } from './services/deviceMetaService';
import { hasPermission, getRoleDisplayName, USER_ROLES } from './utils/roles';
import { trackPageView } from './services/trafficService';

// Map backend role strings to normalized roles.js keys
const normalizeRole = (rawRole, isSuperuser) => {
  if (isSuperuser || rawRole === 'superadmin' || rawRole === 'super_admin') return USER_ROLES.SUPER_ADMIN;
  if (rawRole === 'admin') return USER_ROLES.ADMIN;
  if (rawRole === 'custodian') return USER_ROLES.CUSTODIAN;
  return USER_ROLES.USER;
};

// Per-item accent colors — Climate Pulse brand palette
const NAV_ACCENT = {
  map:          { color: '#1D72B8',  active: 'rgba(29,114,184,0.14)',  label: 'Map' },
  nearest:      { color: '#5bc4a4',  active: 'rgba(91,196,164,0.14)',  label: 'Nearest' },
  dashboard:    { color: '#f0a45a',  active: 'rgba(240,164,90,0.14)',  label: 'Dashboard' },
  analytics:    { color: '#b08fff',  active: 'rgba(176,143,255,0.14)', label: 'Analytics' },
  api:          { color: '#7eb3ff',  active: 'rgba(126,179,255,0.14)', label: 'API Keys' },
  subscription: { color: '#3dd8c0',  active: 'rgba(61,216,192,0.14)',  label: 'Subscription' },
  admin:        { color: '#ff7b8a',  active: 'rgba(255,123,138,0.14)', label: 'Admin' },
  profile:      { color: '#f5c842',  active: 'rgba(245,200,66,0.14)',  label: 'Profile' },
  messages:     { color: '#5bc4a4',  active: 'rgba(91,196,164,0.14)',  label: 'Messages' },
};

const NAV_ITEMS = [
  { key: 'map',          icon: MapPin         },
  { key: 'nearest',      icon: Navigation     },
  { key: 'dashboard',    icon: Activity       },
  { key: 'messages',     icon: MessageSquare  },
  { key: 'analytics',    icon: BarChart3      },
  { key: 'api',          icon: Key            },
  { key: 'subscription', icon: CreditCard     },
];

// ── Mobile bottom nav bar ─────────────────────────────────────────────────────
function MobileBottomNav({ currentView, setCurrentView, navItems, selectedStation, msgUnread, onLogout, userData }) {
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleNav = (key) => {
    if (key === 'dashboard' && !selectedStation) return;
    setCurrentView(key);
  };
  // Show 4 nav items + user/logout button
  const visibleItems = navItems.slice(0, 4);
  return (
    <>
      {/* User menu sheet */}
      {showUserMenu && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setShowUserMenu(false)}>
          <div className="absolute bottom-16 right-2 bg-white rounded-2xl shadow-2xl border border-gray-100 w-52 py-2"
               onClick={e => e.stopPropagation()}>
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-800 truncate">{userData?.name || 'My Account'}</p>
              <p className="text-[11px] text-gray-400 truncate">{userData?.email || ''}</p>
            </div>
            <button
              onClick={() => { setCurrentView('profile'); setShowUserMenu(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 flex items-center gap-3 hover:bg-gray-50"
            >
              <User style={{ width: 15, height: 15, color: '#f59e0b' }} /> Profile
            </button>
            <button
              onClick={() => { onLogout(); setShowUserMenu(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 flex items-center gap-3 hover:bg-red-50"
            >
              <LogOut style={{ width: 15, height: 15 }} /> Logout
            </button>
          </div>
        </div>
      )}

      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {visibleItems.map(({ key, icon: Icon }) => {
          const accent = NAV_ACCENT[key] || NAV_ACCENT.map;
          const isActive = currentView === key;
          const isDisabled = key === 'dashboard' && !selectedStation;
          return (
            <button
              key={key}
              onClick={() => handleNav(key)}
              disabled={isDisabled}
              className={`flex flex-col items-center justify-center py-2 flex-1 relative transition-colors ${isDisabled ? 'opacity-30' : ''}`}
              style={{ color: isActive ? accent.color : '#94a3b8' }}
            >
              <Icon style={{ width: 20, height: 20 }} />
              <span className="text-[10px] font-semibold mt-0.5" style={{ color: isActive ? accent.color : '#94a3b8' }}>
                {accent.label}
              </span>
              {isActive && (
                <span className="absolute top-0 inset-x-0 h-0.5 rounded-b" style={{ background: accent.color }} />
              )}
              {key === 'messages' && msgUnread > 0 && (
                <span className="absolute top-1.5 right-1/4 bg-red-500 text-white text-[8px] font-bold min-w-[14px] h-3.5 rounded-full flex items-center justify-center px-1">
                  {msgUnread > 9 ? '9+' : msgUnread}
                </span>
              )}
            </button>
          );
        })}

        {/* User / logout button */}
        <button
          onClick={() => setShowUserMenu(v => !v)}
          className="flex flex-col items-center justify-center py-2 flex-1 relative transition-colors"
          style={{ color: showUserMenu ? '#f59e0b' : '#94a3b8' }}
        >
          <User style={{ width: 20, height: 20 }} />
          <span className="text-[10px] font-semibold mt-0.5">Me</span>
          {showUserMenu && (
            <span className="absolute top-0 inset-x-0 h-0.5 rounded-b bg-amber-400" />
          )}
        </button>
      </nav>
    </>
  );
}

function Sidebar({ currentView, setCurrentView, userRole, userData, onLogout, selectedStation, darkMode, msgUnread }) {
  const [collapsed, setCollapsed] = useState(false);
  const canAccessAdmin = hasPermission(userRole, 'VIEW_ALL_USERS');
  const canAccessApi = userRole === USER_ROLES.CUSTODIAN ||
    ['research', 'enterprise', 'custom'].includes(userData?.accessTier);

  const navItems = [
    ...NAV_ITEMS.filter(n => n.key !== 'api' || canAccessApi),
    ...(canAccessAdmin ? [{ key: 'admin', icon: Shield }] : []),
  ];

  const handleNav = (key) => {
    if (key === 'dashboard' && !selectedStation) return;
    setCurrentView(key);
  };

  const SidebarContent = () => {
    const bg       = darkMode ? '#0d1f36' : '#0d1f36';
    const headerBg = darkMode ? '#071729' : '#071729';
    const divider  = 'rgba(255,255,255,0.08)';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: bg, color: '#fff' }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', height: 64, padding: collapsed ? '0 12px' : '0 16px',
          justifyContent: collapsed ? 'center' : 'space-between',
          background: headerBg, borderBottom: `1px solid ${divider}`, flexShrink: 0,
        }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <img src="/logo.png" alt="Climate Pulse" style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: '#e8f0fa', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Climate Pulse</p>
                <p style={{ margin: 0, fontSize: 10, color: '#1D72B8', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Weather Intelligence</p>
              </div>
            </div>
          )}
          {collapsed && <img src="/logo.png" alt="Climate Pulse" style={{ width: 28, height: 28, objectFit: 'contain' }} />}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ display: 'none', padding: 6, borderRadius: 7, border: 'none', background: 'rgba(29,114,184,0.12)', cursor: 'pointer', color: '#1D72B8', flexShrink: 0 }}
            className="lg-show"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight style={{ width: 14, height: 14 }} /> : <ChevronLeft style={{ width: 14, height: 14 }} />}
          </button>
          {/* Inline style for lg-show since we can't use media queries in inline style easily */}
          <style>{`.lg-show { display: flex !important; }`}</style>
        </div>

        {/* ── Role Badge ── */}
        {!collapsed && userRole !== USER_ROLES.USER && (
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${divider}` }}>
            {(() => {
              const roleColors = {
                super_admin: { bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.4)', text: '#c084fc' },
                admin:       { bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.4)', text: '#60a5fa' },
                custodian:   { bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.4)',  text: '#4ade80' },
              };
              const rc = roleColors[userRole] || roleColors.custodian;
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: rc.bg, border: `1px solid ${rc.border}`, fontSize: 10, fontWeight: 800, color: rc.text, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: rc.text }} />
                  {getRoleDisplayName(userRole)}
                </span>
              );
            })()}
          </div>
        )}

        {/* ── Navigation ── */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map(({ key, icon: Icon }) => {
            const accent     = NAV_ACCENT[key] || NAV_ACCENT.map;
            const isActive   = currentView === key;
            const isDisabled = key === 'dashboard' && !selectedStation;

            return (
              <button
                key={key}
                onClick={() => handleNav(key)}
                disabled={isDisabled}
                title={isDisabled ? 'Select a station first' : (collapsed ? accent.label : '')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: collapsed ? 0 : 11,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  padding: collapsed ? '11px 8px' : '9px 12px',
                  borderRadius: 10, border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer',
                  background: isActive ? accent.active : 'transparent',
                  boxShadow: isActive ? `inset 3px 0 0 ${accent.color}` : 'none',
                  opacity: isDisabled ? 0.35 : 1,
                  transition: 'background 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={e => !isActive && !isDisabled && (e.currentTarget.style.background = 'rgba(29,114,184,0.07)')}
                onMouseLeave={e => !isActive && (e.currentTarget.style.background = 'transparent')}
              >
                <Icon style={{ width: collapsed ? 19 : 16, height: collapsed ? 19 : 16, color: isActive ? accent.color : '#64748b', flexShrink: 0, transition: 'color 0.15s' }} />
                {!collapsed && (
                  <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? '#f1f5f9' : '#94a3b8', transition: 'color 0.15s' }}>
                    {accent.label}
                  </span>
                )}
                {!collapsed && key === 'dashboard' && selectedStation && (
                  <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: '#1D72B8', boxShadow: '0 0 6px #1D72B8', flexShrink: 0 }} />
                )}
                {key === 'messages' && msgUnread > 0 && (
                  <span style={{ marginLeft: collapsed ? 0 : 'auto', position: collapsed ? 'absolute' : 'static', top: collapsed ? 4 : undefined, right: collapsed ? 4 : undefined, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, minWidth: 15, height: 15, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                    {msgUnread > 99 ? '99+' : msgUnread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* ── Bottom: Notifications + User + Logout ── */}
        <div style={{ borderTop: `1px solid ${divider}`, padding: '10px 8px', flexShrink: 0 }}>
          {/* Notification bell */}
          <div style={{ marginBottom: 4 }}>
            <NotificationBell collapsed={collapsed} onNavigate={setCurrentView} />
          </div>

          {/* User info — click to open profile */}
          {userData && !collapsed && (
            <button
              onClick={() => handleNav('profile')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', margin: '4px 0', borderRadius: 10, background: currentView === 'profile' ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${currentView === 'profile' ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.06)'}`, cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => currentView !== 'profile' && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              onMouseLeave={e => currentView !== 'profile' && (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
            >
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #0d3a6e, #1a5fa8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <User style={{ width: 14, height: 14, color: '#fff' }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userData.fullName || userData.firstName || userData.email}
                </p>
                <p style={{ margin: 0, fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Settings & profile</p>
              </div>
            </button>
          )}
          {userData && collapsed && <div style={{ height: 1, background: divider, margin: '4px 0' }} />}

          {/* Logout */}
          <button
            onClick={onLogout}
            title={collapsed ? 'Logout' : ''}
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: collapsed ? 0 : 10,
              padding: collapsed ? '10px 8px' : '9px 12px',
              borderRadius: 10, border: 'none', background: 'transparent',
              color: '#f87171', cursor: 'pointer', marginTop: 2,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <LogOut style={{ width: 15, height: 15, flexShrink: 0 }} />
            {!collapsed && <span style={{ fontSize: 13, fontWeight: 600 }}>Logout</span>}
          </button>

          {/* Version */}
          {!collapsed && (
            <p style={{ margin: '8px 4px 0', fontSize: 10, color: '#2a5a8a', textAlign: 'center', letterSpacing: '0.05em', fontWeight: 600 }}>
              {process.env.REACT_APP_APP_VERSION || (process.env.REACT_APP_GIT_SHA ? `v1.${process.env.REACT_APP_GIT_SHA.slice(0, 7)}` : 'dev')}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar only — mobile uses MobileBottomNav */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ height: '100vh' }}
      >
        <SidebarContent />
      </aside>

      {/* Mobile bottom nav */}
      <MobileBottomNav
        currentView={currentView}
        setCurrentView={setCurrentView}
        navItems={navItems}
        selectedStation={selectedStation}
        msgUnread={msgUnread}
        onLogout={onLogout}
        userData={userData}
      />
    </>
  );
}

// ── Animated splash screen shown on app open ────────────────────────────────
function SplashOverlay({ visible }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#0d1f36',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'opacity 0.6s ease',
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'all' : 'none',
    }}>
      <img
        src="/logo.png"
        alt="Climate Pulse"
        style={{
          width: 200, height: 200,
          objectFit: 'contain',
          animation: 'splashPulse 1.4s ease-in-out infinite',
        }}
      />
      <style>{`
        @keyframes splashPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.06); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

// ── Onboarding flow (location gate + setup wizard) ────────────────────────────
const STEP_COUNT = 4;

function OnboardingFlow({ station, userId, onDone, onViewStation, onSubscribe }) {
  const [step, setStep]           = useState(0);
  const [locLoading, setLocLoading] = useState(false);
  const [locDenied, setLocDenied]  = useState(false);
  const [locGranted, setLocGranted] = useState(false);
  const [nearestStation, setNearestStation] = useState(station || null);

  useEffect(() => {
    if (station) setNearestStation(station);
  }, [station]);

  // Mark as seen immediately on first render — even if user closes tab without clicking anything
  useEffect(() => {
    if (!userId) return;
    try { localStorage.setItem(`walkthrough_dismissed_${userId}`, '1'); } catch { /* */ }
  }, [userId]);

  const save = () => { try { localStorage.setItem(`walkthrough_dismissed_${userId}`, '1'); } catch { /* */ } };

  const requestLocation = () => {
    setLocLoading(true);
    setLocDenied(false);
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocLoading(false);
        setLocGranted(true);
        try { localStorage.setItem('location_granted', '1'); } catch { /* */ }
        // If nearest not yet known (DevicesMap hasn't fired), wait — it will set it via prop
        setStep(1);
      },
      (err) => {
        setLocLoading(false);
        if (err.code === 1) setLocDenied(true); // PERMISSION_DENIED
        else setStep(1); // timeout / unavailable → continue anyway
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const done = (action) => { save(); onDone(action); };

  // ── Step panels ─────────────────────────────────────────────────────────────

  // 0 — Location gate (mandatory-first)
  const StepLocation = (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#0d3a6e,#1a5fa8)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(13,58,110,0.35)' }}>
        <Navigation style={{ width: 36, height: 36, color: '#7bc8f6' }} />
      </div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900, color: '#111827' }}>Allow Location Access</h2>
      <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
        Climate Pulse uses your location to find the nearest weather station and show you real-time data for your area.
      </p>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', margin: '14px 0 20px', textAlign: 'left' }}>
        {[
          { Icon: MapPin,      text: 'Find the station closest to you' },
          { Icon: Thermometer, text: 'See live weather for your exact region' },
          { Icon: Navigation,  text: 'Get accurate distance & travel time' },
        ].map(({ Icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '5px 0' }}>
            <Icon style={{ width: 15, height: 15, color: '#1D72B8', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 13, color: '#1e3a5f' }}>{text}</p>
          </div>
        ))}
      </div>
      {locDenied ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 14px', marginBottom: 16, textAlign: 'left' }}>
          <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13, color: '#b91c1c' }}>Location was blocked</p>
          <p style={{ margin: 0, fontSize: 12, color: '#991b1b' }}>
            To enable: click the lock/info icon in your browser's address bar → <strong>Allow Location</strong>, then retry.
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#991b1b' }}>
            On mobile: <strong>Settings → Privacy → Location Services</strong> → enable for your browser.
          </p>
          <button onClick={requestLocation} disabled={locLoading}
            style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            {locLoading ? 'Trying…' : 'Retry'}
          </button>
        </div>
      ) : null}
      <button onClick={requestLocation} disabled={locLoading}
        style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: locLoading ? '#dbeafe' : 'linear-gradient(135deg,#0d4a8e,#1a6fc8)', color: locLoading ? '#1d4ed8' : '#fff', fontWeight: 800, fontSize: 15, cursor: locLoading ? 'default' : 'pointer', marginBottom: 10, transition: 'all 0.2s' }}>
        {locLoading ? 'Detecting your location…' : 'Allow Location Access'}
      </button>
      <button onClick={() => setStep(1)}
        style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #e5e7eb', background: 'transparent', color: '#9ca3af', fontWeight: 500, fontSize: 12, cursor: 'pointer' }}>
        Skip — I'll do this later
      </button>
    </div>
  );

  // 1 — Welcome / what you can do
  const StepWelcome = (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img src="/logo.png" alt="Climate Pulse" style={{ width: 56, height: 56, objectFit: 'contain', marginBottom: 10 }} />
        <h2 style={{ margin: '0 0 6px', fontSize: 21, fontWeight: 900, color: '#111827' }}>Welcome to Climate Pulse</h2>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Kenya's real-time weather intelligence network</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
        {[
          { Icon: Map,      color: '#1D72B8', bg: '#eff6ff', title: 'Live Map',         desc: 'View all 28+ active stations across Kenya in real time' },
          { Icon: MapPin,   color: '#0891b2', bg: '#ecfeff', title: 'Nearest Station',  desc: 'See current weather at the station closest to you' },
          { Icon: BarChart3, color: '#7c3aed', bg: '#f5f3ff', title: 'Analytics',       desc: 'Access historical data, trends and downloadable reports' },
        ].map(({ Icon, color, bg, title, desc }) => (
          <div key={title} style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#f9fafb', borderRadius: 10, padding: '10px 12px', border: '1px solid #f3f4f6' }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon style={{ width: 18, height: 18, color }} />
            </div>
            <div>
              <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13, color: '#111827' }}>{title}</p>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setStep(2)}
        style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#0d4a8e,#1a6fc8)', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
        Next →
      </button>
    </div>
  );

  // 2 — Nearest station preview
  const StepNearest = (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: locGranted ? '#ecfdf5' : '#f9fafb', border: `2px solid ${locGranted ? '#6ee7b7' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <MapPin style={{ width: 28, height: 28, color: locGranted ? '#059669' : '#9ca3af' }} />
      </div>
      <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 900, color: '#111827' }}>
        {nearestStation ? 'Your Nearest Station' : 'Explore the Network'}
      </h2>
      {nearestStation ? (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 16px', margin: '12px 0 16px', textAlign: 'left' }}>
          <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Closest to you</p>
          <p style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 900, color: '#14532d' }}>{nearestStation.name}</p>
          {nearestStation.distance != null && (
            <p style={{ margin: 0, fontSize: 13, color: '#1D72B8', fontWeight: 600 }}>{nearestStation.distance.toFixed(1)} km away</p>
          )}
        </div>
      ) : (
        <p style={{ margin: '12px 0 16px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
          {locGranted ? 'Detecting nearest station…' : 'Enable location to see your nearest station.'}
        </p>
      )}
      <button onClick={() => { if (nearestStation) { onViewStation(nearestStation); } else { setStep(3); } }}
        style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#0d4a8e,#1a6fc8)', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: 10 }}>
        {nearestStation ? 'View Live Weather Data' : 'Continue →'}
      </button>
      {nearestStation && (
        <button onClick={() => setStep(3)}
          style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #d1d5db', background: 'transparent', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          Next: Getting Full Access →
        </button>
      )}
    </div>
  );

  // 3 — Setup wizard: steps to get full access
  const setupItems = [
    { done: true,  Icon: CheckCircle,   iconColor: '#16a34a', bg: '#f0fdf4', label: 'Account created',          sub: 'You\'re registered and logged in' },
    { done: false, Icon: ClipboardList, iconColor: '#1D72B8', bg: '#eff6ff', label: 'Get devices assigned',      sub: 'Contact your admin or send a request via Messages' },
    { done: false, Icon: CreditCard,    iconColor: '#7c3aed', bg: '#f5f3ff', label: 'Choose a plan (optional)', sub: 'Basic is free. Upgrade for analytics & API access' },
    { done: false, Icon: Radio,         iconColor: '#0891b2', bg: '#ecfeff', label: 'Access your station data', sub: 'View live readings & historical trends' },
  ];
  const StepSetup = (
    <div>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 900, color: '#111827', textAlign: 'center' }}>Getting Full Access</h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
        Here's what you need to do to get your stations assigned.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {setupItems.map(({ done: itemDone, Icon, iconColor, bg, label, sub }, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 13px', borderRadius: 10, background: itemDone ? '#f0fdf4' : '#f9fafb', border: `1px solid ${itemDone ? '#86efac' : '#e5e7eb'}` }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon style={{ width: 17, height: 17, color: iconColor }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13, color: itemDone ? '#15803d' : '#111827' }}>{label}</p>
              <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>{sub}</p>
            </div>
            {itemDone && <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20, flexShrink: 0 }}>Done</span>}
          </div>
        ))}
      </div>
      <button onClick={onSubscribe}
        style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#0d4a8e,#1a6fc8)', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: 10 }}>
        View Subscription Plans
      </button>
      <button onClick={() => done('map')}
        style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #d1d5db', background: 'transparent', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
        Explore the Map Now
      </button>
    </div>
  );

  const panels = [StepLocation, StepWelcome, StepNearest, StepSetup];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 22, padding: '30px 26px 26px', maxWidth: 420, width: '100%', boxShadow: '0 28px 70px rgba(0,0,0,0.30)', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 26, justifyContent: 'center' }}>
          {Array.from({ length: STEP_COUNT }).map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? '#1D72B8' : '#e5e7eb', transition: 'background 0.3s' }} />
          ))}
        </div>
        {panels[step]}
        {/* Dismiss (only from step 1 onwards) */}
        {step > 0 && (
          <button onClick={() => done('map')}
            style={{ position: 'absolute', top: 14, right: 14, border: 'none', background: 'none', color: '#9ca3af', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4, borderRadius: 6 }}>
            ×
          </button>
        )}
        {/* Step label */}
        <p style={{ textAlign: 'center', margin: '16px 0 0', fontSize: 11, color: '#d1d5db', fontWeight: 600 }}>
          STEP {step + 1} OF {STEP_COUNT}
        </p>
      </div>
    </div>
  );
}

function App() {
  const [splashVisible, setSplashVisible] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('map');
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedStationInfo, setSelectedStationInfo] = useState(null);
  const [userRole, setUserRole] = useState(USER_ROLES.USER);
  const [userData, setUserData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('darkMode') === 'true'; } catch { return false; }
  });
  const [msgUnread, setMsgUnread] = useState(0);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughStation, setWalkthroughStation] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('darkMode', darkMode); } catch { /* ignore */ }
  }, [darkMode]);

  useEffect(() => {
    const t = setTimeout(() => setSplashVisible(false), 2500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    checkAuthStatus();
    loadDeviceMeta(); // load admin-saved name/location overrides from backend
  }, []);

  useEffect(() => {
    if (currentView === 'nearest' || currentView === 'map') {
      loadDevices();
    }
    trackPageView(currentView);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  const walkthroughKey = (userId) => `walkthrough_dismissed_${userId}`;

  const checkAuthStatus = async () => {
    if (authService.isAuthenticated()) {
      setIsAuthenticated(true);
      const user = authService.getCurrentUser();
      if (user) {
        setUserData(user);
        const role = normalizeRole(user.role, user.is_superuser);
        setUserRole(role);
        // Show onboarding only if this specific user has never dismissed it
        if (role === USER_ROLES.USER || role === USER_ROLES.CUSTODIAN) {
          try {
            if (!localStorage.getItem(walkthroughKey(user._id || user.id || user.email))) {
              setShowWalkthrough(true);
            }
          } catch { /* ignore */ }
        }
      }
    }
  };

  const loadDevices = async () => {
    if (devices.length > 0) return;
    setLoadingDevices(true);
    try {
      const devicesData = await fetchWeatherDevices();
      setDevices(devicesData);
    } catch (error) {
      console.error('Failed to load devices in App:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    setIsAuthenticated(true);
    setUserData(userData);
    const role = normalizeRole(userData?.role, userData?.is_superuser);
    setUserRole(role);
    setDevices([]);
    loadDevices();
    checkAuthStatus();
    // Show onboarding only if this specific user has never dismissed it
    if (role === USER_ROLES.USER || role === USER_ROLES.CUSTODIAN) {
      try {
        const userId = userData?.id || userData?._id || userData?.email;
        if (userId && !localStorage.getItem(walkthroughKey(userId))) setShowWalkthrough(true);
      } catch { /* ignore */ }
    }
  };

  const handleNearestFound = (station) => {
    setWalkthroughStation(station);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUserRole(USER_ROLES.USER);
    setUserData(null);
    setCurrentView('map');
    setSelectedStation(null);
    setDevices([]);
    setShowWalkthrough(false);
    // Note: walkthrough_dismissed_<userId> is intentionally NOT removed on logout
    // so returning users never see the walkthrough again on their next login.
  };

  const handleStationSelect = (stationId, stationInfo) => {
    setSelectedStation(stationId);
    setSelectedStationInfo(stationInfo);
    setCurrentView('dashboard');
  };

  if (!isAuthenticated) {
    return (
      <>
        <SplashOverlay visible={splashVisible} />
        <LandingPage onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  // Compute which device IDs the logged-in user is allowed to see.
  // null  = no restriction (admins/super_admins, or research/enterprise tier)
  // []    = no devices assigned yet
  // [ids] = restricted to assigned stations
  const getAllowedDeviceIds = () => {
    if (!isAuthenticated) return null;
    if (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN) return null;
    // Custodians see only their assigned stations (with full analytics on those)
    if (userRole === USER_ROLES.CUSTODIAN) return userData?.assignedDevices ?? [];
    const tier = userData?.accessTier;
    if (tier === 'research' || tier === 'enterprise' || tier === 'custom') return null;
    return userData?.assignedDevices ?? [];
  };

  // Whether the current user can access analytics
  const canAccessAnalytics = () => {
    if (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN) return true;
    if (userRole === USER_ROLES.CUSTODIAN) return true;
    const tier = userData?.accessTier;
    if (!tier || tier === 'basic') return false;
    return true;
  };
  const allowedDeviceIds = getAllowedDeviceIds();

  const renderContent = () => {
    switch (currentView) {
      case 'map':
        return (
          <DevicesMap
            onLoginSuccess={handleLoginSuccess}
            onStationSelect={handleStationSelect}
            allowedDeviceIds={allowedDeviceIds}
            userRole={userRole}
            onNearestFound={handleNearestFound}
            onNavigate={setCurrentView}
          />
        );

      case 'nearest': {
        const nearestDevices = allowedDeviceIds
          ? devices.filter(d => allowedDeviceIds.includes(d.stationId || d.id))
          : devices;
        return (
          <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-4xl mx-auto">
              {loadingDevices ? (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading weather stations...</p>
                </div>
              ) : nearestDevices.length === 0 ? (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                  <Navigation className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-700 font-semibold mb-1">No Stations Assigned</p>
                  <p className="text-sm text-gray-500">Contact your admin to get weather stations assigned to your account.</p>
                </div>
              ) : (
                <NearestStationsPanel
                  stations={nearestDevices}
                  onStationSelect={handleStationSelect}
                />
              )}
            </div>
          </div>
        );
      }

      case 'dashboard':
        return selectedStation ? (
          <WeatherStationDashboard
            stationId={selectedStation}
            stationInfo={selectedStationInfo}
            onBack={() => setCurrentView('map')}
            userData={userData}
            userRole={userRole}
          />
        ) : (
          <DevicesMap onLoginSuccess={handleLoginSuccess} onStationSelect={handleStationSelect} userRole={userRole} />
        );

      case 'analytics':
        if (!canAccessAnalytics()) {
          return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 max-w-sm w-full text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-amber-500" />
                </div>
                <h2 className="text-lg font-black text-gray-900 mb-2">Analytics Locked</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Analytics requires an Education plan or higher. Upgrade your subscription to access historical charts and data export.
                </p>
                <button
                  onClick={() => setCurrentView('subscription')}
                  className="w-full py-2.5 bg-emerald-600 text-white font-semibold rounded-xl text-sm hover:bg-emerald-700 transition-colors"
                >
                  View Plans
                </button>
              </div>
            </div>
          );
        }
        return <WeatherAnalyticsDashboard stationId={selectedStation} />;

      case 'api':
        return (
          <ApiKeysPage
            userData={userData}
            userRole={userRole}
            onNavigate={setCurrentView}
          />
        );

      case 'subscription':
        return (
          <SubscriptionPage
            userData={userData}
            onUserUpdate={(updated) => {
              setUserData(prev => ({ ...prev, ...updated }));
            }}
          />
        );

      case 'profile':
        return (
          <ProfilePage
            userData={userData}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            onUserUpdate={(updated) => setUserData(prev => ({ ...prev, ...updated }))}
          />
        );

      case 'messages':
        return (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <MessagingPanel onUnreadChange={setMsgUnread} />
          </div>
        );

      default:
        return (
          <DevicesMap onLoginSuccess={handleLoginSuccess} onStationSelect={handleStationSelect} />
        );
    }
  };

  // Admins & super admins get the full-screen admin dashboard — no app sidebar
  if (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN || currentView === 'admin') {
    return (
      <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} requiredPermission="VIEW_ALL_USERS">
        <SuperAdminDashboard
          userRole={userRole}
          userData={userData}
          onLogout={handleLogout}
        />
      </ProtectedRoute>
    );
  }

  return (
    <>
      <SplashOverlay visible={splashVisible} />
      {showWalkthrough && (
        <OnboardingFlow
          station={walkthroughStation}
          userId={userData?._id || userData?.id || userData?.email}
          onDone={(action) => {
            setShowWalkthrough(false);
            if (action === 'map') setCurrentView('map');
          }}
          onViewStation={(station) => {
            const uid = userData?._id || userData?.id || userData?.email;
            try { if (uid) localStorage.setItem(`walkthrough_dismissed_${uid}`, '1'); } catch { /* */ }
            setShowWalkthrough(false);
            handleStationSelect(station.stationId || station.id, station);
          }}
          onSubscribe={() => {
            const uid = userData?._id || userData?.id || userData?.email;
            try { if (uid) localStorage.setItem(`walkthrough_dismissed_${uid}`, '1'); } catch { /* */ }
            setShowWalkthrough(false);
            setCurrentView('subscription');
          }}
        />
      )}
      <div className="flex h-screen w-screen overflow-hidden bg-gray-100">
        <Sidebar
          currentView={currentView}
          setCurrentView={setCurrentView}
          userRole={userRole}
          userData={userData}
          onLogout={handleLogout}
          selectedStation={selectedStation}
          darkMode={darkMode}
          msgUnread={msgUnread}
        />
        <main className="flex-1 overflow-auto min-w-0 pb-16 lg:pb-0">
          {renderContent()}
        </main>
      </div>
    </>
  );
}

export default App;
